import { Alert, Button, Grid, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import React, { ChangeEvent, useRef } from "react";
import { toast } from "react-toastify";

interface XmlUploaderProps {
  idInput: string;
  buttonText: string;
  fileObject: any | null;
  setFileObject: React.Dispatch<React.SetStateAction<any | null>>;
  disabled?: boolean;
  onFileParsed?: (facturaObj: any) => void;
  clearForm?: () => void
  validateProveedor?: (ruc: string, razonSocial: string) => boolean;
  validateTipoComprobante?: (codigo: string) => boolean;
  validateFormaPago?: (codigo: string) => boolean;
  inputRef?: React.RefObject<HTMLInputElement>
}

const XmlUploader: React.FC<XmlUploaderProps> = ({
  idInput,
  buttonText,
  fileObject,
  setFileObject,
  disabled = false,
  onFileParsed,
  clearForm,
  validateProveedor,
  validateTipoComprobante,
  validateFormaPago,
  inputRef
}) => {
  const fileInputRef = inputRef || useRef<HTMLInputElement>(null);

  const parseXml = (xmlStr: string) => {
    let xml = new DOMParser().parseFromString(xmlStr, "application/xml");
    //revisa si es el primer formato(autorizacion)
    const autorizacion = xml.getElementsByTagName("autorizacion")[0]
    if (autorizacion) {
      const comprobanteCdata = autorizacion.getElementsByTagName("comprobante")[0]?.textContent || "";
      if (comprobanteCdata) {
        xml = new DOMParser().parseFromString(comprobanteCdata, "application/xml")
      }
    }
    // Info Tributaria
    const infoTributaria = xml.getElementsByTagName("infoTributaria")[0];
    const ruc = infoTributaria?.getElementsByTagName("ruc")[0]?.textContent || "";
    const razonSocial = infoTributaria?.getElementsByTagName("razonSocial")[0]?.textContent || "";
    const codDoc = infoTributaria?.getElementsByTagName("codDoc")[0]?.textContent || "";
    const claveAcceso = infoTributaria?.getElementsByTagName("claveAcceso")[0]?.textContent || "";

    // Info Factura
    const infoFactura = xml.getElementsByTagName("infoFactura")[0];
    const fechaEmision = infoFactura?.getElementsByTagName("fechaEmision")[0]?.textContent || "";
    const pagos = infoFactura?.getElementsByTagName("pagos")[0];
    const formaPagoCodigo = pagos?.getElementsByTagName("formaPago")[0]?.textContent || "";

    return {
      Proveedor: { ruc, razonSocial },
      TipoComprobante: { codigo: codDoc },
      FormaPago: { codigo: formaPagoCodigo },
      NumeroAutorizacion: claveAcceso,
      FechaEmision: fechaEmision,
    };
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.name.endsWith(".xml")) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const facturaObj = parseXml(content);

      // Validaciones
      if (validateProveedor && !validateProveedor(facturaObj.Proveedor.ruc, facturaObj.Proveedor.razonSocial)) {
        toast.error(`Proveedor no encontrado: RUC ${facturaObj.Proveedor.ruc}, razon social "${facturaObj.Proveedor.razonSocial}". Debes registrarlo en el módulo de proveedores antes de poder cargar este XML.`);
        return;
      }
      if (validateTipoComprobante && !validateTipoComprobante(facturaObj.TipoComprobante.codigo)) {
        toast.error(
          `Código SRI "${facturaObj.TipoComprobante.codigo}" de Tipo de comprobante no registrado. ` +
          `Regístralo en el módulo correspondiente antes de subir el XML.`
        );
        return;
      }
      if (validateFormaPago && !validateFormaPago(facturaObj.FormaPago.codigo)) {
        toast.error(
          `Código SRI "${facturaObj.FormaPago.codigo}" de forma de pago no registrado. ` +
          `Debes registrarlo en el módulo de Forma de Pago Proveedor antes de cargar el XML.`
        );
      }

      setFileObject(facturaObj);
      if (onFileParsed) onFileParsed(facturaObj);
    };
    reader.readAsText(file);
  };

  const handleRemove = () => {
    setFileObject(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (clearForm) clearForm();
  };

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={12}>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          id={`xml-uploader-${idInput}`}
          accept=".xml"
          disabled={disabled}
          onChange={handleFileChange}
        />
        <label htmlFor={`xml-uploader-${idInput}`}>
          <Button
            fullWidth
            variant="outlined"
            component="span"
            startIcon={<UploadIcon />}
            disabled={disabled}
          >
            {buttonText}
          </Button>
        </label>
      </Grid>

      {fileObject && (
        <Grid item xs={12}>
          <Alert
            severity="success"
            action={
              <IconButton size="small" onClick={handleRemove}>
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            }
          >
            Archivo cargado correctamente
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default XmlUploader;
